import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { MailCheck, CheckCircle2 } from 'lucide-react';
import kabbiLogo from '@/assets/images/kabbi-logo-gelb.png';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import i18n from '../i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Same VITE_API_URL handling as lib/api.ts and authStore.ts — split-domain
// prod points this at https://api.kabbi.io/api; dev falls back to same-origin.
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api';

const RESEND_COOLDOWN = 60;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const email = searchParams.get('email') ?? '';

  const [code, setCode] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startCooldown();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      if (!res.ok) { toast.error(t('verify.errorInvalid')); return; }
      setSuccess(true);
      toast.success(t('verify.success'));
      setTimeout(() => navigate('/login'), 2500);
    } catch {
      toast.error(t('verify.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await fetch(`${API_BASE}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, locale: i18n.language }),
      });
      toast.success(t('verify.resent'));
      startCooldown();
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm space-y-8">
        <div className="flex items-center gap-2">
          <img src={kabbiLogo} alt="Kabbi" className="h-14" />
        </div>

        <div className="flex flex-col items-center text-center space-y-2">
          <div className="bg-primary/10 rounded-full p-4">
            <MailCheck className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold">{t('verify.title')}</h2>
          <p className="text-muted-foreground text-sm">
            {t('verify.subtitle')} <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>

        {success ? (
          <div className="flex flex-col items-center gap-3 text-center py-4">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
            <p className="text-sm font-medium text-green-700">{t('verify.success')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="code">{t('verify.codeLabel')}</Label>
              <Input
                id="code"
                placeholder={t('verify.codePlaceholder')}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                disabled={loading}
                className="h-11 text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                inputMode="numeric"
                autoFocus
              />
            </div>

            <Button type="submit" className="w-full h-11" disabled={loading || code.length !== 6}>
              {loading
                ? <span className="flex items-center gap-2"><span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />{t('verify.loading')}</span>
                : t('verify.submit')}
            </Button>

            <div className="flex flex-col items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                className="w-full h-10"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
              >
                {resending
                  ? <span className="flex items-center gap-2"><span className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />{t('verify.resending')}</span>
                  : cooldown > 0
                    ? t('verify.resendIn', { seconds: cooldown })
                    : t('verify.resend')}
              </Button>
            </div>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="text-primary font-medium hover:underline">{t('register.loginLinkAction')}</Link>
        </p>
      </div>
    </div>
  );
}
